import { useState, useEffect, useCallback } from 'react';
import { Client } from '@hiveio/dhive';
import HIVE_RPC_NODES from '../config/hiveNodes';
import hiveService from './hive';

const client = new Client(HIVE_RPC_NODES);

const parseAsset = (asset) => {
  if (!asset || typeof asset !== 'string') {
    return { amount: null, symbol: null };
  }
  const [amount, symbol] = asset.split(' ');
  return {
    amount: amount ? parseFloat(amount) : null,
    symbol: symbol || null
  };
};

const getHivePerHbd = (price) => {
  const base = parseAsset(price?.base);
  const quote = parseAsset(price?.quote);
  if (!base.amount || !quote.amount || !base.symbol || !quote.symbol) return null;

  if (base.symbol === 'HBD' && quote.symbol === 'HIVE') return quote.amount / base.amount;
  if (base.symbol === 'HIVE' && quote.symbol === 'HBD') return base.amount / quote.amount;
  return null;
};

const usePostBlockchainData = ({ post, user, isAuthenticated, rewardDisplayMode, rewardDisplayCurrency, initialReplyCount }) => {
  const [voteCount, setVoteCount] = useState(post?.votes?.total || post?.votes?.up || 0);
  const [downvoteCount, setDownvoteCount] = useState(post?.votes?.down || 0);
  const [replyCount, setReplyCount] = useState(initialReplyCount !== undefined ? initialReplyCount : (post?.replies?.length ?? post?.replies ?? 0));
  const [hasVoted, setHasVoted] = useState(false);
  const [userVoteWeight, setUserVoteWeight] = useState(null);
  
  const [pendingRewardHive, setPendingRewardHive] = useState(0);
  const [pendingRewardHbd, setPendingRewardHbd] = useState(0);
  const [totalPayoutHive, setTotalPayoutHive] = useState(0);
  const [totalPayoutHbd, setTotalPayoutHbd] = useState(0);
  const [cashoutTime, setCashoutTime] = useState(null);
  const [activeVotesWithValue, setActiveVotesWithValue] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchBlockchainData = async () => {
      if (!post?.author || !post?.permlink) return;
      if (isMounted) setRewardsLoading(true);
      
      try {
        const [content, activeVotes, dynamicProps, medianHistoryPrice] = await Promise.all([
          client.database.call('get_content', [post.author, post.permlink]),
          client.database.call('get_active_votes', [post.author, post.permlink]),
          client.database.call('get_dynamic_global_properties', []),
          client.call('condenser_api', 'get_current_median_history_price', [])
        ]);
        
        if (!isMounted) return;

        // Update vote and reply counts
        setVoteCount(activeVotes.filter(v => v.percent > 0).length);
        setDownvoteCount(activeVotes.filter(v => v.percent < 0).length);
        if (content && content.children !== undefined) setReplyCount(content.children);
        
        // Rewards conversions
        let rewardHive = 0;
        let pendingHBD = 0;
        if (content && content.pending_payout_value) {
          pendingHBD = parseFloat(content.pending_payout_value);
          const dynamicRate = getHivePerHbd(dynamicProps?.hbd_exchange_rate);
          const medianRate = getHivePerHbd(medianHistoryPrice);
          const hbdExchangeRate = dynamicRate ?? medianRate ?? 1;
          
          rewardHive = pendingHBD * hbdExchangeRate;
          setPendingRewardHbd(pendingHBD);
          setPendingRewardHive(rewardHive);

          const totalPayoutValue = parseFloat(content.total_payout_value) || 0;
          const curatorPayoutValue = parseFloat(content.curator_payout_value) || 0;
          setTotalPayoutHbd(totalPayoutValue + curatorPayoutValue);
          setTotalPayoutHive((totalPayoutValue + curatorPayoutValue) * hbdExchangeRate);
        }
        
        if (content && content.cashout_time) setCashoutTime(new Date(content.cashout_time + 'Z'));
        
        if (activeVotes && content && (rewardHive > 0 || pendingHBD > 0)) {
          const netRshares = parseFloat(content.net_rshares) || 0;
          setActiveVotesWithValue(activeVotes.map(vote => {
            const share = netRshares > 0 ? ((parseFloat(vote.rshares) || 0) / netRshares) : 0;
            return { ...vote, reward_hive: share > 0 ? share * rewardHive : 0, reward_hbd: share > 0 ? share * pendingHBD : 0 };
          }));
        }
        
        if (isAuthenticated && user?.username) {
          const userVote = activeVotes.find(v => v.voter === user.username);
          setHasVoted(!!userVote && userVote.percent > 0);
          setUserVoteWeight(userVote && userVote.percent ? Math.max(1, Math.round(userVote.percent / 100)) : null);
        }
      } catch (error) {
        console.error('[BlockchainFetch] Error fetching blockchain data:', error?.message);
      } finally {
        if (isMounted) setRewardsLoading(false);
      }
    };
    
    fetchBlockchainData();
    return () => { isMounted = false; };
  }, [post?.author, post?.permlink, isAuthenticated, user?.username, rewardDisplayMode, rewardDisplayCurrency]);

  // Unified vote caster
  const castVote = useCallback(async (percent, isUpvote = true) => {
    if (!user?.username || !post?.author || !post?.permlink) throw new Error('Missing post or user data');
    const safePercent = typeof percent === 'number' && !isNaN(percent) ? percent : 100;
    const weight = isUpvote ? Math.round(safePercent * 100) : -Math.round(safePercent * 100);
    return await hiveService.vote(user.username, post.author, post.permlink, weight);
  }, [user?.username, post?.author, post?.permlink]);

  return { voteCount, setVoteCount, downvoteCount, replyCount, hasVoted, setHasVoted, userVoteWeight, setUserVoteWeight, pendingRewardHive, pendingRewardHbd, totalPayoutHive, totalPayoutHbd, cashoutTime, activeVotesWithValue, rewardsLoading, castVote };
};

export default usePostBlockchainData;